import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { EventsApiService } from '@presentationServices';
import { SentryLoggerService } from '@shared';
import { of } from 'rxjs';
import { catchError, map, mergeMap, withLatestFrom } from 'rxjs/operators';
import * as EventsActions from './events.actions';
import {
  selectInPreparationEventsPagination,
  selectPastEventsPagination,
  selectUpcomingEventsPagination,
} from './events.selectors';

@Injectable()
export class EventsEffects {
  private actions$: Actions = inject(Actions);
  private store: Store = inject(Store);
  private eventsService: EventsApiService = inject(EventsApiService);
  private sentryLogger: SentryLoggerService = inject(SentryLoggerService);

  loadUpcomingEvents$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventsActions.loadUpcomingEvents),
      mergeMap(({ page, search, tagIds, dateFrom, dateTo }) =>
        this.eventsService.getUpcomingEvents(page, 20, search, tagIds, dateFrom, dateTo).pipe(
          map((response) => EventsActions.loadUpcomingEventsSuccess({
            events: response.items,
            total: response.total,
            page: response.page,
            totalPages: response.totalPages,
            limit: response.limit,
            append: page > 1,
          })),
          catchError((error: any) => {
            this.sentryLogger.captureHttpError(error, {
              component: 'EventsEffects',
              action: 'loadUpcomingEvents',
            });
            return of(EventsActions.loadUpcomingEventsFailure({ error: 'Failed to load upcoming events' }));
          }),
        ),
      ),
    ),
  );

  loadPastEvents$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventsActions.loadPastEvents),
      mergeMap(({ page, search, tagIds, dateFrom, dateTo }) =>
        this.eventsService.getPastEvents(page, 20, search, tagIds, dateFrom, dateTo).pipe(
          map((response) => EventsActions.loadPastEventsSuccess({
            events: response.items,
            total: response.total,
            page: response.page,
            totalPages: response.totalPages,
            limit: response.limit,
            append: page > 1,
          })),
          catchError((error: any) => {
            this.sentryLogger.captureHttpError(error, {
              component: 'EventsEffects',
              action: 'loadPastEvents',
            });
            return of(EventsActions.loadPastEventsFailure({ error: 'Failed to load past events' }));
          }),
        ),
      ),
    ),
  );

  loadMorePastEvents$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventsActions.loadMorePastEvents),
      withLatestFrom(this.store.select(selectPastEventsPagination)),
      map(([ action, pagination ]) => EventsActions.loadPastEvents({
        search: action.search,
        tagIds: action.tagIds,
        dateFrom: action.dateFrom,
        dateTo: action.dateTo,
        page: pagination.currentPage + 1,
      })),
    ),
  );

  loadMoreUpcomingEvents$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventsActions.loadMoreUpcomingEvents),
      withLatestFrom(this.store.select(selectUpcomingEventsPagination)),
      map(([ action, pagination ]) => EventsActions.loadUpcomingEvents({
        search: action.search,
        tagIds: action.tagIds,
        dateFrom: action.dateFrom,
        dateTo: action.dateTo,
        page: pagination.currentPage + 1,
      })),
    ),
  );

  loadInPreparationEvents$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventsActions.loadInPreparationEvents),
      mergeMap(({ page, search, tagIds, dateFrom, dateTo }) =>
        this.eventsService.getPreparationEvents(page, 20, search, tagIds, dateFrom, dateTo).pipe(
          map((response) => EventsActions.loadInPreparationEventsSuccess({
            events: response.items,
            total: response.total,
            page: response.page,
            totalPages: response.totalPages,
            limit: response.limit,
            append: page > 1,
          })),
          catchError((error: any) => {
            this.sentryLogger.captureHttpError(error, {
              component: 'EventsEffects',
              action: 'loadInPreparationEvents',
            });
            return of(EventsActions.loadInPreparationEventsFailure({ error: 'Failed to load in preparation events' }));
          }),
        ),
      ),
    ),
  );

  loadMoreInPreparationEvents$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventsActions.loadMoreInPreparationEvents),
      withLatestFrom(this.store.select(selectInPreparationEventsPagination)),
      map(([ action, pagination ]) => EventsActions.loadInPreparationEvents({
        search: action.search,
        tagIds: action.tagIds,
        dateFrom: action.dateFrom,
        dateTo: action.dateTo,
        page: pagination.currentPage + 1,
      })),
    ),
  );
}
